import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'

import { Alert, Loader, Box } from '@components/ui'

export const STATUS_MAP = {
  ready: 'ready',
  pending: 'pending',
  resolved: 'resolved',
  error: 'error',
}

export const isPendingStatus = (status) => status === STATUS_MAP.pending
export const isResolvedStatus = (status) => status === STATUS_MAP.resolved
export const isErrorStatus = (status) => status === STATUS_MAP.error

export const PromiseRequestWrapper = ({ status, children }) => {
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (!initialized && isResolvedStatus(status)) {
      setInitialized(true)
    }
  }, [status])

  if (isErrorStatus(status)) {
    return <Alert severity="error">An error has occured</Alert>
  }

  if (!isResolvedStatus(status) && !initialized) {
    return <Loader.Centered />
  }

  const opacity = isPendingStatus(status) ? 0.8 : 1

  return <Box sx={{ opacity, transition: 'opacity .25s ease-in-out' }}>{children}</Box>
}

PromiseRequestWrapper.propTypes = {
  status: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

const DEFAULT_STATE = {
  status: STATUS_MAP.ready,
  response: null,
  error: null,
}

const usePromise = (func, initialState = {}) => {
  const [state, setState] = useState({
    ...DEFAULT_STATE,
    ...initialState,
  })

  const onSuccess = (response) => {
    setState({
      ...state,
      ...DEFAULT_STATE,
      status: STATUS_MAP.resolved,
      response,
    })
  }

  const onError = (error) => {
    setState({
      ...state,
      ...DEFAULT_STATE,
      status: STATUS_MAP.error,
      error: error.message,
    })
  }

  const wrappedFunction = (...args) => {
    setState({
      ...state,
      ...DEFAULT_STATE,
      status: STATUS_MAP.pending,
    })

    return func(...args).then(onSuccess, onError)
  }

  return [state, wrappedFunction]
}

export default usePromise
